import React, { useState, useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'

import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import BottomButtonGroup, { BUTTON_LOADING_ICON, FORM_COMPACT_DANGER_ACTION_BUTTON_SX } from '../../component/BottomButtonGroup'
import ListEmptyState from '../../component/ListEmptyState'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'

const PANEL_SX = {
    p: 2,
    borderRadius: '4px',
    borderColor: 'rgba(148, 163, 184, 0.24)',
    backgroundColor: '#fff',
}

function FormSection({ title, description, children, sx = {} }) {
    return (
        <Paper variant="outlined" sx={{ ...PANEL_SX, ...sx }}>
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {description}
                </Typography>
            </Box>
            {children}
        </Paper>
    )
}

function EventImagePreview({ imageSrc }) {
    return (
        <FormSection
            title="이벤트 리뷰 이미지"
            description="포인트 지급 처리 전 사용자가 등록한 이미지를 확인합니다."
            sx={{ height: '100%' }}
        >
            <Box
                sx={{
                    minHeight: 340,
                    maxHeight: 520,
                    borderRadius: '4px',
                    border: '1px solid rgba(148, 163, 184, 0.24)',
                    backgroundColor: '#f8fafc',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {imageSrc ? (
                    <Box
                        component="img"
                        src={imageSrc}
                        alt="이벤트 리뷰 이미지"
                        sx={{
                            width: '100%',
                            maxHeight: 520,
                            objectFit: 'contain',
                            display: 'block',
                        }}
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        등록된 이미지가 없습니다.
                    </Typography>
                )}
            </Box>
        </FormSection>
    )
}

export default function AppReviewEvent(props) {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const id = props.match.params.reviewEventId
    const curPage = locationState.curPage || 1
    const searchText = locationState.searchText || ''
    const searchSelect = locationState.searchSelect || { searchStatus: '', searchDeviceType: '' }
    const listSearch = location.search || ''
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [actionType, setActionType] = useState('')
    const [hasDetail, setHasDetail] = useState(false)
    const [params, setParams] = useState({})
    const [image, setImage] = useState('')
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    const isPointUpdating = saving && actionType === 'update'
    const isRejecting = saving && actionType === 'reject'
    const isDeleting = saving && actionType === 'delete'

    useEffect(() => {
        const getData = async () => {
            if (!id) {
                const errorMessage = '필수 파라미터가 없어 리뷰 이벤트 상세를 불러올 수 없습니다.'
                setParams({})
                setImage('')
                setHasDetail(false)
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
                setLoading(false)
                return
            }

            setLoading(true)
            setHasDetail(false)
            setDetailErrorMessage('')

            try {
                const { data } = await API.post('/api/v1/admin/appReviewEvent/getDetail', { reviewEventId: id })
                if (data.code === 200) {
                    const baseParams = data.data || {}

                    if (!baseParams.reviewEventId) {
                        setParams({})
                        setImage('')
                        setDetailErrorMessage('리뷰 이벤트 상세 정보를 찾을 수 없습니다.')
                        setLoading(false)
                        return
                    }

                    setParams(baseParams)
                    setImage(baseParams.image ? `${process.env.REACT_APP_AWS_S3_URL}/${baseParams.image}` : '/images/noimage.jpg')
                    setHasDetail(true)
                    setDetailErrorMessage('')
                } else if (data.code === 403) {
                    setLoading(false)
                    history.push('/login')
                    return
                } else {
                    const errorMessage = data.message || '리뷰 이벤트 상세를 불러오지 못했습니다.'
                    setParams({})
                    setImage('')
                    setDetailErrorMessage(errorMessage)
                    setOpenSnackbar(true)
                    setSeveritySnackbar('error')
                    setMessageSnackbar(errorMessage)
                }
            } catch (_error) {
                const errorMessage = '리뷰 이벤트 상세를 불러오지 못했습니다.'
                setParams({})
                setImage('')
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }

            setLoading(false)
        }
        getData()
    }, [history, id])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const moveToList = (snackbarState) => {
        // 액션 후에도 운영자가 보던 리뷰 이벤트 목록 조건으로 그대로 복귀시킨다.
        history.push(createListLinkTarget({
            pathname: '/appReviewEvents',
            search: listSearch,
            curPage: curPage,
            searchText: searchText,
            searchSelect: searchSelect,
            ...(snackbarState ? {
                openSnackbar: true,
                severitySnackbar: snackbarState.severity,
                messageSnackbar: snackbarState.message,
            } : {})
        }))
    }

    const runAction = async ({ endpoint, type, successMessage, errorMessage }) => {
        if (saving) {
            return
        }

        try {
            setSaving(true)
            setActionType(type)

            const { data } = await API.post(endpoint, { reviewEventId: id })
            if (data.code === 200) {
                moveToList({ severity: 'success', message: successMessage })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setSaving(false)
            setActionType('')
        }
    }

    const updateData = async () => {
        await runAction({
            endpoint: '/api/v1/admin/appReviewEvent/update',
            type: 'update',
            successMessage: '리뷰 이벤트 포인트 지급이 완료되었습니다.',
            errorMessage: '리뷰 이벤트를 승인하지 못했습니다.',
        })
    }

    const rejectData = async () => {
        await runAction({
            endpoint: '/api/v1/admin/appReviewEvent/reject',
            type: 'reject',
            successMessage: '리뷰 이벤트를 보류 처리했습니다.',
            errorMessage: '리뷰 이벤트를 보류하지 못했습니다.',
        })
    }

    const deleteData = async () => {
        await runAction({
            endpoint: '/api/v1/admin/appReviewEvent/delete',
            type: 'delete',
            successMessage: '리뷰 이벤트를 삭제했습니다.',
            errorMessage: '리뷰 이벤트를 삭제하지 못했습니다.',
        })
    }

    // 상세 조회 실패 시 목록 화면과 같은 오류 빈 상태 안내를 사용한다.
    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="리뷰 이벤트 상세를 불러오지 못했습니다."
            description={detailErrorMessage || '목록으로 돌아가 다시 확인하세요.'}
            action={(
                <BottomButtonGroup variant="emptyStateAction" path="/appReviewEvents" search={listSearch} visible={false} searchText={searchText} searchSelect={searchSelect} curPage={curPage} backLabel="목록으로"/>
            )}
            sx={{ mt: 2 }}
        />
    )

    if (loading) {
        return (
            <Grid item xs={12}>
                <Spiner/>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Grid>
        )
    }

    if (!hasDetail) {
        return (
            <Grid item xs={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>리뷰이벤트 상세</Title>
                    {renderDetailErrorState()}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            </Grid>
        )
    }

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Title>리뷰이벤트 상세</Title>
                <Box sx={{ opacity: saving ? 0.6 : 1 }}>
                    <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        <Grid item xs={12} md={5}>
                            <EventImagePreview imageSrc={image}/>
                        </Grid>
                        <Grid item xs={12} md={7}>
                            <FormSection
                                title="신청 정보"
                                description="신청자, 등록일, 기기, 처리 상태를 확인합니다."
                                sx={{ height: '100%' }}
                            >
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth>
                                            <TextField
                                                label="userId"
                                                id="userId"
                                                name="userId"
                                                value={params.userId || params.user?.userId || ''}
                                                size="small"
                                                InputProps={{ readOnly: true }}
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth>
                                            <TextField
                                                label="이름"
                                                id="username"
                                                name="username"
                                                value={params.user?.username || ''}
                                                size="small"
                                                InputProps={{ readOnly: true }}
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth>
                                            <TextField
                                                label="닉네임"
                                                id="nickName"
                                                name="nickName"
                                                value={params.user?.nickName || ''}
                                                size="small"
                                                InputProps={{ readOnly: true }}
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth>
                                            <TextField
                                                label="이메일"
                                                id="email"
                                                name="email"
                                                value={params.email || params.user?.email || ''}
                                                size="small"
                                                InputProps={{ readOnly: true }}
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth>
                                            <TextField
                                                label="등록일"
                                                id="regTime"
                                                name="regTime"
                                                value={params.regTime || ''}
                                                size="small"
                                                InputProps={{ readOnly: true }}
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth>
                                            <TextField
                                                label="기기"
                                                id="deviceType"
                                                name="deviceType"
                                                value={params.deviceType || ''}
                                                size="small"
                                                InputProps={{ readOnly: true }}
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth>
                                            <TextField
                                                label="상태"
                                                id="status"
                                                name="status"
                                                value={params.status === 1 ? '미확인' : params.status === 2 ? '지급완료' : params.status === 3 ? '보류' : ''}
                                                size="small"
                                                InputProps={{ readOnly: true }}
                                            />
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </FormSection>
                        </Grid>
                    </Grid>
                    <BottomButtonGroup
                        variant="formAction"
                        visible={false}
                        backLabel="뒤로"
                        backDisabled={saving}
                        onBackClick={() => moveToList()}
                        actionTitle="리뷰 이벤트 처리"
                        actionDescription="이미지와 신청 정보를 확인한 뒤 지급, 보류, 삭제 처리를 진행합니다."
                        extraActions={(
                            <>
                                {params.status === 1 && (
                                    <Button
                                        type="button"
                                        onClick={updateData}
                                        variant="contained"
                                        color="primary"
                                        disabled={saving}
                                        startIcon={isPointUpdating ? BUTTON_LOADING_ICON : undefined}
                                        aria-busy={isPointUpdating}
                                    >
                                        {isPointUpdating ? '지급 중...' : '포인트지급'}
                                    </Button>
                                )}
                                {params.status === 1 && (
                                    <Button
                                        type="button"
                                        onClick={rejectData}
                                        variant="contained"
                                        color="error"
                                        sx={FORM_COMPACT_DANGER_ACTION_BUTTON_SX}
                                        disabled={saving}
                                        startIcon={isRejecting ? BUTTON_LOADING_ICON : undefined}
                                        aria-busy={isRejecting}
                                    >
                                        {isRejecting ? '보류 중...' : '보류'}
                                    </Button>
                                )}
                                {params.status === 2 && (
                                    <Button
                                        type="button"
                                        onClick={deleteData}
                                        variant="contained"
                                        color="error"
                                        sx={FORM_COMPACT_DANGER_ACTION_BUTTON_SX}
                                        disabled={saving}
                                        startIcon={isDeleting ? BUTTON_LOADING_ICON : undefined}
                                        aria-busy={isDeleting}
                                    >
                                        {isDeleting ? '삭제 중...' : '삭제'}
                                    </Button>
                                )}
                            </>
                        )}
                    />
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
